import React, { useState } from 'react';
import { AlertCircle, CheckCircle2, Loader2, Sparkles } from 'lucide-react';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import Button from '../../../components/ui/Button';
import { Checkbox } from '../../../components/ui/Checkbox';
import FormProgress from './FormProgress';
import {
  FormData,
  FormErrors,
  BUSINESS_TYPES,
  ESTABLISHMENT_SIZES,
  MARKETING_CHANNELS,
  OFFER_TYPES,
  CUSTOMER_VOLUMES,
} from '../types';
import partnerApplicationService from '../../../services/partnerApplicationService';

const STEPS = [
  { id: 1, title: 'Business Info' },
  { id: 2, title: 'Business Details' },
  { id: 3, title: 'Partnership Interest' },
  { id: 4, title: 'Documentation' },
];

const ApplicationForm: React.FC = () => {
  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState<FormData>({
    business_name: '',
    business_type: '',
    business_address: '',
    contact_name: '',
    contact_email: '',
    contact_phone: '',
    establishment_size: '',
    business_description: '',
    target_demographics: '',
    current_marketing_channels: [],
    website_url: '',
    social_media_urls: {},
    desired_offer_types: [],
    expected_customer_volume: '',
    marketing_goals: '',
    business_license_number: '',
    tax_id: '',
    vat_number: '',
    applicant_notes: '',
  });
  const [errors, setErrors] = useState<FormErrors>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [submitError, setSubmitError] = useState('');

  // Add prefill function for development
  const handlePrefillForm = () => {
    const getRandomItem = <T,>(array: T[]): T => {
      return array[Math.floor(Math.random() * array.length)];
    };

    // PRODUCTION FIX: Avoid spread operators - use manual loop instead
    const getRandomItems = <T,>(array: T[], count: number): T[] => {
      const result: T[] = [];
      const availableIndices: number[] = [];
      
      // Build index array manually
      for (let i = 0; i < array.length; i++) {
        availableIndices.push(i);
      }
      
      // Shuffle manually without spread operator
      for (let i = availableIndices.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        const temp = availableIndices[i];
        availableIndices[i] = availableIndices[j];
        availableIndices[j] = temp;
      }
      
      // Take first 'count' items
      for (let i = 0; i < Math.min(count, availableIndices.length); i++) {
        result.push(array[availableIndices[i]]);
      }
      
      return result;
    };

    const randomBusinessNames = [
      'Vienna Delights Café',
      'Habsburg Heritage Hotel',
      'Schönbrunn Souvenirs',
      'Danube Wellness Spa',
      'Mozart Concert Hall',
      'Imperial Palace Restaurant',
      'Alpine Adventure Tours',
      'Vienna Art Gallery',
      'Prater Park Bistro',
      'Ringstrasse Boutique Hotel'
    ];

    const randomAddresses = [
      'Mariahilfer Straße 123, 1060 Vienna',
      'Kärntner Ring 45, 1010 Vienna',
      'Landstraßer Hauptstraße 78, 1030 Vienna',
      'Westbahnstraße 92, 1070 Vienna',
      'Favoritenstraße 156, 1100 Vienna',
      'Währinger Straße 234, 1090 Vienna'
    ];

    const randomNames = [
      'Franz Mueller',
      'Maria Schmidt',
      'Johann Wagner',
      'Anna Bauer',
      'Stefan Fischer',
      'Elisabeth Schneider'
    ];

    const randomDescriptions = [
      'We offer an authentic Viennese experience with traditional cuisine and modern hospitality. Our establishment combines historic charm with contemporary amenities, making it perfect for tourists seeking genuine local culture.',
      'A family-owned business dedicated to providing exceptional service and unique experiences. We specialize in catering to international visitors with multilingual staff and curated cultural programs.',
      'Our venue showcases the best of Vienna through carefully crafted offerings. We pride ourselves on attention to detail and creating memorable moments for every guest who walks through our doors.',
      'Located in the heart of Vienna, we provide premium services with a personal touch. Our team is passionate about sharing Vienna\'s rich heritage while meeting modern traveler expectations.'
    ];

    const randomDemographics = [
      'Young professionals, Cultural enthusiasts, International tourists',
      'Families with children, Senior travelers, History buffs',
      'Solo travelers, Digital nomads, Adventure seekers',
      'Luxury travelers, Business professionals, Art lovers'
    ];

    const randomMarketingGoals = [
      'Increase tourist foot traffic during off-peak seasons, build brand awareness among international visitors, establish long-term customer relationships through quality service and memorable experiences.',
      'Expand market reach to younger demographics, leverage digital marketing for better visibility, create buzz through innovative promotional campaigns and partnerships.',
      'Drive repeat business through loyalty programs, enhance online reputation with positive reviews, position our brand as a must-visit destination in Vienna tourist guides.',
      'Boost revenue during weekdays, attract high-value customers, differentiate from competitors through unique value propositions and exclusive offerings.'
    ];

    const randomTimestamp = Date.now();
    const randomEmail = `test.partner${randomTimestamp}@example.com`;

    const prefillData: FormData = {
      business_name: getRandomItem(randomBusinessNames),
      business_type: getRandomItem(BUSINESS_TYPES),
      business_address: getRandomItem(randomAddresses),
      contact_name: getRandomItem(randomNames),
      contact_email: randomEmail,
      contact_phone: `+43 ${Math.floor(Math.random() * 900 + 100)} ${Math.floor(Math.random() * 9000000 + 1000000)}`,
      establishment_size: getRandomItem(ESTABLISHMENT_SIZES),
      business_description: getRandomItem(randomDescriptions),
      target_demographics: getRandomItem(randomDemographics),
      current_marketing_channels: getRandomItems(MARKETING_CHANNELS, Math.floor(Math.random() * 4) + 2),
      website_url: `https://www.example-business-${randomTimestamp}.com`,
      social_media_urls: {
        facebook: `https://facebook.com/business${randomTimestamp}`,
        instagram: `https://instagram.com/business${randomTimestamp}`,
        linkedin: `https://linkedin.com/company/business${randomTimestamp}`
      },
      desired_offer_types: getRandomItems(OFFER_TYPES, Math.floor(Math.random() * 4) + 2),
      expected_customer_volume: getRandomItem(CUSTOMER_VOLUMES),
      marketing_goals: getRandomItem(randomMarketingGoals),
      business_license_number: `BL${Math.floor(Math.random() * 900000 + 100000)}`,
      tax_id: `AT${Math.floor(Math.random() * 90000000 + 10000000)}`,
      vat_number: `ATU${Math.floor(Math.random() * 90000000 + 10000000)}`,
      applicant_notes: 'This is a test application generated for development purposes. All information is randomly generated and does not represent a real business.'
    };

    setFormData(prefillData);
    setErrors({});
    setCurrentStep(1);
  };

  const validateStep = (step: number): boolean => {
    const newErrors: FormErrors = {};

    if (step === 1) {
      if (!formData.business_name.trim()) newErrors.business_name = 'Business name is required';
      if (!formData.business_type) newErrors.business_type = 'Business type is required';
      if (!formData.business_address.trim()) newErrors.business_address = 'Address is required';
      if (!formData.contact_name.trim()) newErrors.contact_name = 'Contact name is required';
      if (!formData.contact_email.trim()) {
        newErrors.contact_email = 'Email is required';
      } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.contact_email)) {
        newErrors.contact_email = 'Invalid email format';
      }
      if (!formData.contact_phone.trim()) newErrors.contact_phone = 'Phone is required';
    }

    if (step === 2) {
      if (!formData.establishment_size) newErrors.establishment_size = 'Establishment size is required';
      if (!formData.business_description.trim()) {
        newErrors.business_description = 'Business description is required';
      } else if (formData.business_description.length < 50) {
        newErrors.business_description = 'Description must be at least 50 characters';
      }
      if (!formData.target_demographics.trim()) {
        newErrors.target_demographics = 'Target demographics is required';
      }
      if (formData.current_marketing_channels.length === 0) {
        newErrors.current_marketing_channels = 'Select at least one marketing channel';
      }
    }

    if (step === 3) {
      if (formData.desired_offer_types.length === 0) {
        newErrors.desired_offer_types = 'Select at least one offer type';
      }
      if (!formData.expected_customer_volume) {
        newErrors.expected_customer_volume = 'Expected customer volume is required';
      }
      if (!formData.marketing_goals.trim()) {
        newErrors.marketing_goals = 'Marketing goals are required';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = () => {
    if (validateStep(currentStep)) {
      setCurrentStep((prev) => Math.min(prev + 1, STEPS.length));
    }
  };

  const handleBack = () => {
    setCurrentStep((prev) => Math.max(prev - 1, 1));
  };

  const handleChange = (field: keyof FormData, value: any) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: '' }));
    }
  };

  const handleCheckboxChange = (field: 'current_marketing_channels' | 'desired_offer_types', value: string) => {
    setFormData((prev) => {
      const currentArray = prev[field] as string[];
      let newArray: string[];
      
      // PRODUCTION FIX: Manual array operations without spread operator
      if (currentArray.includes(value)) {
        // Remove item - use filter (it's safe)
        newArray = currentArray.filter((item) => item !== value);
      } else {
        // Add item - manual construction without spread
        newArray = [];
        for (let i = 0; i < currentArray.length; i++) {
          newArray.push(currentArray[i]);
        }
        newArray.push(value);
      }
      
      return { ...prev, [field]: newArray };
    });
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: '' }));
    }
  };

  const handleSubmit = async () => {
    if (!validateStep(currentStep)) return;

    setIsSubmitting(true);
    setSubmitError('');

    try {
      // CRITICAL FIX: No destructuring - direct property access to prevent minification issues
      const checkResult = await partnerApplicationService.checkExistingApplication(
        formData.contact_email
      );

      // Use direct property access instead of destructuring
      if (checkResult.exists === true && checkResult.data) {
        setSubmitError(
          `An application with this email already exists (Status: ${checkResult.data.status}). Please contact support if you need assistance.`
        );
        setIsSubmitting(false);
        return;
      }

      // Submit application
      const result = await partnerApplicationService.submitApplication(formData);

      setIsSubmitting(false);

      if (result.success) {
        setSubmitSuccess(true);
      } else {
        setSubmitError(result.error || 'Failed to submit application. Please try again.');
      }
    } catch (error: any) {
      console.error('Submit error:', error);
      setSubmitError('An unexpected error occurred. Please try again.');
      setIsSubmitting(false);
    }
  };

  if (submitSuccess) {
    return (
      <div className="max-w-2xl mx-auto text-center py-12">
        <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <CheckCircle2 className="w-10 h-10 text-green-600" />
        </div>
        <h2 className="text-2xl font-bold text-gray-900 mb-3">
          Application Submitted Successfully!
        </h2>
        <p className="text-gray-600 mb-6">
          Thank you for your interest in partnering with TravelHeroes. We have received your application
          and will review it within 2-3 business days. You will receive an email at{' '}
          <strong>{formData.contact_email}</strong> with the next steps.
        </p>
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
          <h3 className="font-semibold text-blue-900 mb-2">What happens next?</h3>
          <ul className="text-sm text-blue-800 text-left space-y-1">
            <li>• Our team will review your application details</li>
            <li>• We may contact you for additional information</li>
            <li>• Once approved, you will receive login credentials</li>
            <li>• You can start creating offers immediately after approval</li>
          </ul>
        </div>
        <Button onClick={() => window.location.reload()}>
          Submit Another Application
        </Button>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto">
      <FormProgress currentStep={currentStep} steps={STEPS} />

      {/* Prefill Button - Always visible */}
      <div className="mb-4">
        <Button
          variant="secondary"
          onClick={handlePrefillForm}
          className="w-full"
        >
          <Sparkles className="w-4 h-4 mr-2" />
          Prefill with Random Data (Dev Only)
        </Button>
      </div>

      {submitError && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start space-x-3">
          <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <p className="text-sm text-red-800">{submitError}</p>
          </div>
        </div>
      )}

      <div className="bg-white rounded-lg shadow-sm p-8">
        {/* Step 1: Business Information */}
        {currentStep === 1 && (
          <div className="space-y-6">
            <div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">Business Information</h3>
              <p className="text-gray-600">Tell us about your business</p>
            </div>

            <Input
              label="Business Name *"
              value={formData.business_name}
              onChange={(e) => handleChange('business_name', e.target.value)}
              error={errors.business_name}
              placeholder="Enter your business name"
            />

            <Select
              label="Business Type *"
              value={formData.business_type}
              onChange={(value) => handleChange('business_type', value)}
              error={errors.business_type}
              options={[
                { value: '', label: 'Select business type' },
                ...BUSINESS_TYPES.map((type) => ({ value: type, label: type })),
              ]}
            />

            <Input
              label="Business Address *"
              value={formData.business_address}
              onChange={(e) => handleChange('business_address', e.target.value)}
              error={errors.business_address}
              placeholder="Full address in Vienna"
            />

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Input
                label="Contact Name *"
                value={formData.contact_name}
                onChange={(e) => handleChange('contact_name', e.target.value)}
                error={errors.contact_name}
                placeholder="Your full name"
              />

              <Input
                label="Contact Phone *"
                value={formData.contact_phone}
                onChange={(e) => handleChange('contact_phone', e.target.value)}
                error={errors.contact_phone}
                placeholder="+43 XXX XXXXXXX"
              />
            </div>

            <Input
              label="Contact Email *"
              type="email"
              value={formData.contact_email}
              onChange={(e) => handleChange('contact_email', e.target.value)}
              error={errors.contact_email}
              placeholder="your@email.com"
            />
          </div>
        )}

        {/* Step 2: Business Details */}
        {currentStep === 2 && (
          <div className="space-y-6">
            <div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">Business Details</h3>
              <p className="text-gray-600">Help us understand your business better</p>
            </div>

            <Select
              label="Establishment Size *"
              value={formData.establishment_size}
              onChange={(value) => handleChange('establishment_size', value)}
              error={errors.establishment_size}
              options={[
                { value: '', label: 'Select size' },
                ...ESTABLISHMENT_SIZES.map((size) => ({ value: size, label: size })),
              ]}
            />

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Business Description * (minimum 50 characters)
              </label>
              <textarea
                value={formData.business_description}
                onChange={(e) => handleChange('business_description', e.target.value)}
                rows={4}
                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 ${
                  errors.business_description ? 'border-red-300' : 'border-gray-300'
                }`}
                placeholder="Describe your business, what makes it unique, and why tourists should visit..."
              />
              <div className="flex justify-between mt-1">
                {errors.business_description && (
                  <p className="text-sm text-red-600">{errors.business_description}</p>
                )}
                <p className="text-sm text-gray-500 ml-auto">
                  {formData.business_description.length} characters
                </p>
              </div>
            </div>

            <Input
              label="Target Demographics *"
              value={formData.target_demographics}
              onChange={(e) => handleChange('target_demographics', e.target.value)}
              error={errors.target_demographics}
              placeholder="e.g., Young professionals, Families, Cultural enthusiasts"
            />

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-3">
                Current Marketing Channels * (select all that apply)
              </label>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                {MARKETING_CHANNELS.map((channel) => (
                  <Checkbox
                    key={channel}
                    label={channel}
                    checked={formData.current_marketing_channels.includes(channel)}
                    onChange={() => handleCheckboxChange('current_marketing_channels', channel)}
                  />
                ))}
              </div>
              {errors.current_marketing_channels && (
                <p className="text-sm text-red-600 mt-2">{errors.current_marketing_channels}</p>
              )}
            </div>

            <Input
              label="Website URL"
              value={formData.website_url}
              onChange={(e) => handleChange('website_url', e.target.value)}
              placeholder="https://your-website.com"
            />

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-3">
                Social Media Links
              </label>
              <div className="space-y-3">
                <Input
                  label="Facebook"
                  value={formData.social_media_urls?.facebook || ''}
                  onChange={(e) =>
                    handleChange('social_media_urls', {
                      ...formData.social_media_urls,
                      facebook: e.target.value,
                    })
                  }
                  placeholder="https://facebook.com/your-page"
                />
                <Input
                  label="Instagram"
                  value={formData.social_media_urls?.instagram || ''}
                  onChange={(e) =>
                    handleChange('social_media_urls', {
                      ...formData.social_media_urls,
                      instagram: e.target.value,
                    })
                  }
                  placeholder="https://instagram.com/your-profile"
                />
                <Input
                  label="LinkedIn"
                  value={formData.social_media_urls?.linkedin || ''}
                  onChange={(e) =>
                    handleChange('social_media_urls', {
                      ...formData.social_media_urls,
                      linkedin: e.target.value,
                    })
                  }
                  placeholder="https://linkedin.com/company/your-company"
                />
              </div>
            </div>
          </div>
        )}

        {/* Step 3: Partnership Interest */}
        {currentStep === 3 && (
          <div className="space-y-6">
            <div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">Partnership Interest</h3>
              <p className="text-gray-600">What kind of offers would you like to create?</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-3">
                Desired Offer Types * (select all that apply)
              </label>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                {OFFER_TYPES.map((type) => (
                  <Checkbox
                    key={type}
                    label={type}
                    checked={formData.desired_offer_types.includes(type)}
                    onChange={() => handleCheckboxChange('desired_offer_types', type)}
                  />
                ))}
              </div>
              {errors.desired_offer_types && (
                <p className="text-sm text-red-600 mt-2">{errors.desired_offer_types}</p>
              )}
            </div>

            <Select
              label="Expected Monthly Customer Volume *"
              value={formData.expected_customer_volume}
              onChange={(value) => handleChange('expected_customer_volume', value)}
              error={errors.expected_customer_volume}
              options={[
                { value: '', label: 'Select expected volume' },
                ...CUSTOMER_VOLUMES.map((volume) => ({ value: volume, label: volume })),
              ]}
            />

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Marketing Goals *
              </label>
              <textarea
                value={formData.marketing_goals}
                onChange={(e) => handleChange('marketing_goals', e.target.value)}
                rows={4}
                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 ${
                  errors.marketing_goals ? 'border-red-300' : 'border-gray-300'
                }`}
                placeholder="What do you hope to achieve through this partnership? (e.g., increase tourist foot traffic, build brand awareness, etc.)"
              />
              {errors.marketing_goals && (
                <p className="text-sm text-red-600 mt-1">{errors.marketing_goals}</p>
              )}
            </div>
          </div>
        )}

        {/* Step 4: Documentation */}
        {currentStep === 4 && (
          <div className="space-y-6">
            <div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">Documentation & Additional Info</h3>
              <p className="text-gray-600">Optional but recommended for faster approval</p>
            </div>

            <Input
              label="Business License Number"
              value={formData.business_license_number}
              onChange={(e) => handleChange('business_license_number', e.target.value)}
              placeholder="Your business license number"
            />

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Input
                label="Tax ID"
                value={formData.tax_id}
                onChange={(e) => handleChange('tax_id', e.target.value)}
                placeholder="Tax identification number"
              />

              <Input
                label="VAT Number"
                value={formData.vat_number}
                onChange={(e) => handleChange('vat_number', e.target.value)}
                placeholder="ATU12345678"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Additional Notes
              </label>
              <textarea
                value={formData.applicant_notes}
                onChange={(e) => handleChange('applicant_notes', e.target.value)}
                rows={4}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                placeholder="Any additional information you would like to share with us..."
              />
            </div>

            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <h4 className="font-semibold text-blue-900 mb-2">Review Your Application</h4>
              <p className="text-sm text-blue-800 mb-3">
                Before submitting, please review all information for accuracy. Our team will contact you
                within 2-3 business days.
              </p>
              <div className="grid grid-cols-2 gap-2 text-sm">
                <div>
                  <span className="text-blue-600 font-medium">Business:</span>{' '}
                  <span className="text-blue-900">{formData.business_name}</span>
                </div>
                <div>
                  <span className="text-blue-600 font-medium">Type:</span>{' '}
                  <span className="text-blue-900">{formData.business_type}</span>
                </div>
                <div>
                  <span className="text-blue-600 font-medium">Contact:</span>{' '}
                  <span className="text-blue-900">{formData.contact_name}</span>
                </div>
                <div>
                  <span className="text-blue-600 font-medium">Email:</span>{' '}
                  <span className="text-blue-900">{formData.contact_email}</span>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Navigation Buttons */}
        <div className="flex justify-between mt-8 pt-6 border-t border-gray-200">
          <Button
            variant="secondary"
            onClick={handleBack}
            disabled={currentStep === 1 || isSubmitting}
          >
            Back
          </Button>

          {currentStep < STEPS.length ? (
            <Button onClick={handleNext} disabled={isSubmitting}>
              Next Step
            </Button>
          ) : (
            <Button onClick={handleSubmit} disabled={isSubmitting}>
              {isSubmitting ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Submitting...
                </>
              ) : (
                'Submit Application'
              )}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

export default ApplicationForm;